/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { graphql } from 'react-apollo';

import { withCalendarData } from '@coursera-web/calendar-sync';
import { compose } from 'recompose';

import user from 'js/lib/user';

import { Button, Divider, Typography2 } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import {
  AppleCalendarIcon,
  GoogleCalendarIcon,
  OtherCalendarIcon,
} from 'bundles/account-settings/components/sections/calendar/providerIcons';
import ToggleShouldDisplayCalendarMutation from 'bundles/account-settings/components/sections/calendar/queries/ToggleShouldDisplayCalendarMutation.graphql';
import type {
  ToggleCalendarEnabledv2Mutation,
  ToggleCalendarEnabledv2MutationVariables,
} from 'bundles/account-settings/components/sections/calendar/queries/__generated__/ToggleShouldDisplayCalendarMutation';

import _t from 'i18n!nls/account-settings';

type InnerProps = {
  toggleCalendarEnabled: (x0: {}) => Promise<unknown>;
  calendar?: {
    google: string;
    webcal: string;
    ics: string;
  };
};

const withCalendarToggleMutation = graphql<
  {},
  ToggleCalendarEnabledv2Mutation,
  ToggleCalendarEnabledv2MutationVariables,
  {}
>(ToggleShouldDisplayCalendarMutation, {
  name: 'toggleCalendarEnabled',
});

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  heading: css`
    margin: 0;
  `,
  description: css`
    margin: 4px 0 0;
  `,
  list: css`
    list-style: none;
    margin: 16px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  row: css`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px;
  `,
  rowLabel: css`
    flex: 1 0 0;
    min-width: 0;
  `,
};

export const CalendarSettings = ({ calendar, toggleCalendarEnabled }: InnerProps) => {
  const track = useTracker();
  const [loadingButton, setLoadingButton] = useState<string | null>(null);

  const calendarOptions: Array<{
    name: ButtonName;
    label: string;
    ariaLabel: string;
    link: string | undefined;
    icon: () => React.ReactElement;
  }> = [
    {
      name: 'account_setting_download_cal_google_btn',
      label: _t('Google calendar'),
      ariaLabel: _t('Add Google calendar'),
      link: calendar?.google,
      icon: GoogleCalendarIcon,
    },
    {
      name: 'account_setting_download_cal_apple_btn',
      label: _t('Apple calendar'),
      ariaLabel: _t('Add Apple calendar'),
      link: calendar?.webcal,
      icon: AppleCalendarIcon,
    },
    {
      name: 'account_setting_download_cal_other_btn',
      label: _t('Other calendar'),
      ariaLabel: _t('Add other calendar'),
      link: calendar?.webcal,
      icon: OtherCalendarIcon,
    },
  ];

  const handleCalendarButtonClick = (link: string | undefined, trackingName: ButtonName) => {
    if (!link) {
      return;
    }

    setLoadingButton(trackingName);

    track('click_button', {
      button: { name: trackingName },
    });

    toggleCalendarEnabled({
      variables: {
        body: {
          userId: user.get().id.toString(),
          shouldDisplayContent: true,
        },
      },
      context: { clientName: 'gatewayGql' },
    }).then(() => {
      window.open(link, '_blank');
      setLoadingButton(null);
    });
  };

  return (
    <div css={styles.container}>
      <Typography2 variant="subtitleLarge" component="h2" id="a11y-navigation-heading" css={styles.heading}>
        {_t('Calendar sync')}
      </Typography2>
      <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.description}>
        {_t('Automatically sync all the deadlines and other related items from all active courses to your calendar.')}
      </Typography2>
      <ul role="list" css={styles.list}>
        {calendarOptions.map((option, i) => {
          const Icon = option.icon;
          return (
            <React.Fragment key={option.name}>
              {i > 0 && <Divider color="light" orientation="horizontal" />}
              <li role="listitem" css={styles.row}>
                <Icon />
                <Typography2 variant="subtitleMedium" component="span" css={styles.rowLabel}>
                  {option.label}
                </Typography2>
                <Button
                  size="small"
                  variant="secondary"
                  loading={loadingButton === option.name}
                  onClick={() => handleCalendarButtonClick(option.link, option.name)}
                  aria-label={option.ariaLabel}
                >
                  {_t('Add calendar')}
                </Button>
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};

// TODO: withCalendarData has no hook equivalent — it combines a query, auto-creation mutation,
// and URL transformation. Migrate both HOCs to hooks once calendar-sync exposes a useCalendarData hook.
export default compose<InnerProps, {}>(withCalendarToggleMutation, withCalendarData)(CalendarSettings);
